import { Head, Link } from "@inertiajs/react";
import { Stack, Group, Button, Grid, Anchor, ThemeIcon } from "@mantine/core";
import {
  IconBrandInstagram,
  IconExternalLink,
  IconMusic,
  IconUsers,
  IconMapPin,
  IconDeviceLaptop,
} from "@tabler/icons-react";
import PageShell from "../../Components/Layout/PageShell";
import PageBackgroundPhoto from "../../Components/Layout/PageBackgroundPhoto";
import { LatestPostsStrip } from "../../Components/LatestPostsStrip";
import {
  Heading1,
  Heading2,
  Heading3,
  DimmedText,
  MainText,
} from "../../Components/UI/Typography";
import { Card } from "../../Components/UI/Card";
import { Badge } from "../../Components/UI/Badge";

const PROJECTS = [
  {
    name: "Learntokiz",
    url: "https://learntokiz.io",
    tagline: "Online Urban Kiz Education",
    description:
      "A dedicated platform for learning Urban Kizomba (Neokizomba) online — structured lessons, technique breakdowns, and musicality training for dancers at every level.",
    icon: IconDeviceLaptop,
    color: "#b8843d",
    badge: "primary" as const,
    badgeLabel: "Online Platform",
    cta: "Visit Learntokiz",
  },
  {
    name: "WhereCanWeDance",
    url: "https://wherecanwedance.com/artists/ogarocious",
    tagline: "Austin's Social Dance Directory",
    description:
      "A community event directory connecting social dancers in Austin to classes, socials, and workshops across kizomba, salsa, bachata, and more.",
    icon: IconMapPin,
    color: "#d4552b",
    badge: "tools" as const,
    badgeLabel: "Community Project",
    cta: "View My Profile",
  },
];

const OFFERINGS = [
  {
    icon: IconUsers,
    title: "Private Lessons",
    description:
      "One-on-one instruction tailored to your level and goals. Build foundation, refine technique, or develop your personal movement language.",
  },
  {
    icon: IconMusic,
    title: "Group Workshops",
    description:
      "Immersive workshop experiences covering Urban Kizomba fundamentals, musicality, connection, and partner dynamics in a collaborative setting.",
  },
  {
    icon: IconDeviceLaptop,
    title: "Online Instruction",
    description:
      "Can't make it in person? Learn Urban Kiz on your own schedule via Learntokiz — structured curriculum accessible from anywhere.",
  },
];

export default function DanceIndex() {
  return (
    <>
      <Head>
        <title>Dance | Ogarocious</title>
        <meta
          name="description"
          content="Urban Kiz educator — private lessons, Learntokiz, WCWD, and the Austin dance community."
        />
        <meta property="og:title" content="Dance | Ogarocious" />
        <meta
          property="og:description"
          content="Urban Kiz educator — private lessons, Learntokiz, WCWD, and the Austin dance community."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${import.meta.env.VITE_SITE_URL || ""}/dance`}
        />
        <meta
          property="og:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dance | Ogarocious" />
        <meta
          name="twitter:description"
          content="Urban Kiz educator — private lessons, Learntokiz, WCWD."
        />
        <meta
          name="twitter:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
      </Head>

      <PageShell
        variant="section"
        extraBackgrounds={
          <PageBackgroundPhoto src="/images/dance-hero.jpg" alt="Charles teaching Urban Kiz at LABF" />
        }
      >
        <Stack gap={80}>
            {/* ── Hero ── */}
            <Stack gap="lg" align="center">
              <Badge variant="skills" size="lg">
                Urban Kizomba · Neokizomba · Austin, TX
              </Badge>
              <Heading1 variant="hero" style={{ textAlign: "center" }}>
                Dance
              </Heading1>
              <DimmedText
                size="xl"
                style={{
                  textAlign: "center",
                  maxWidth: "clamp(300px, 85vw, 560px)",
                  lineHeight: 1.6,
                }}
              >
                I teach Urban Kiz — a contemporary African dance form built on
                connection, musicality, and self-expression. Based in Austin,
                teaching worldwide.
              </DimmedText>
              <Group gap="md" mt="xs">
                <Button
                  component={Link}
                  href="/contact"
                  variant="gradient"
                  gradient={{ from: "#b8843d", to: "#d4552b", deg: 135 }}
                  size="lg"
                >
                  Book a Lesson
                </Button>
                <Button
                  component="a"
                  href="https://instagram.com/mr.neokiz"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline"
                  size="lg"
                  leftSection={<IconBrandInstagram size={18} />}
                  styles={{
                    root: {
                      borderColor: "rgba(212,85,43,0.4)",
                      color: "#b8843d",
                    },
                  }}
                >
                  @mr.neokiz
                </Button>
              </Group>
            </Stack>

            {/* ── Bio ── */}
            <Card padding="xl" radius="md">
              <Stack gap="md">
                <MainText style={{ lineHeight: 1.75 }}>
                  I've been building Austin's Urban Kiz and Kizomba community since 2014. What started as a passion for an African diasporic artform became a decade of organizing, teaching, producing, and traveling — co-founding NeoKizomba and Kiz ATX, running one of Austin's most consistent weekly practicas, and producing NeoKiz Festival for eight editions.
                </MainText>
                <MainText style={{ lineHeight: 1.75 }}>
                  Urban Kizomba isn't just a dance style — it's a lineage rooted in Angolan Kizomba, evolved through the African diaspora, and bridged to Black American culture through R&B and Hip-Hop. I take that seriously in how I teach and what I amplify. My approach goes beyond steps: I teach musicality, movement theory, and the cultural history that gives this artform its meaning. Understanding <em>why</em> a movement exists — and where it came from — changes how you dance it.
                </MainText>
                <MainText style={{ lineHeight: 1.75 }}>
                  My approach in the room puts connection before technique: understanding how to listen, respond, and move with someone is the foundation everything else builds on. NeoKiz was the flagship — workshops by day, socials by night, Jack and Jill competitions that the community still talks about years later. I traveled nationally and internationally to teach, brought instructors to Austin, and watched the community grow past what I could take credit for.
                </MainText>
                <MainText style={{ lineHeight: 1.75 }}>
                  The continuation is Kiz Summit Exchange (KSX) — a 10-day intensive co-produced with Evokeez Paris, bringing a European cohort of 20–30 dancers to Austin. KSX 2026 runs October 24 to November 2. As a two-time recipient of the City of Austin AACME Elevate Grant, this work is recognized as a cultural contribution to the city — not just an event.
                </MainText>
              </Stack>
            </Card>

            {/* ── What I Teach ── */}
            <Stack gap="xl">
              <Stack gap="xs" align="center">
                <Heading2 variant="shadow" style={{ textAlign: "center" }}>
                  What I Teach
                </Heading2>
                <DimmedText style={{ textAlign: "center", maxWidth: 480 }}>
                  Urban Kizomba (Neokizomba) is a contemporary evolution of
                  Kizomba rooted in African music and social dance culture. It
                  emphasizes fluidity, improvisation, and deep musical
                  connection between partners.
                </DimmedText>
              </Stack>

              <Grid gutter="md">
                {OFFERINGS.map((item) => (
                  <Grid.Col key={item.title} span={{ base: 12, sm: 4 }}>
                    <Card padding="xl" radius="md" h="100%">
                      <Stack gap="md">
                        <ThemeIcon
                          size={48}
                          radius="md"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(212,85,43,0.3), rgba(212,85,43,0.1))",
                            border: "1px solid rgba(212,85,43,0.2)",
                          }}
                        >
                          <item.icon size={24} style={{ color: "#b8843d" }} />
                        </ThemeIcon>
                        <Heading3>{item.title}</Heading3>
                        <DimmedText style={{ lineHeight: 1.65 }}>
                          {item.description}
                        </DimmedText>
                      </Stack>
                    </Card>
                  </Grid.Col>
                ))}
              </Grid>
            </Stack>

            {/* ── Projects ── */}
            <Stack gap="xl">
              <Stack gap="xs" align="center">
                <Heading2 variant="shadow" style={{ textAlign: "center" }}>
                  Dance Projects
                </Heading2>
                <DimmedText style={{ textAlign: "center", maxWidth: 480 }}>
                  Beyond teaching, I build infrastructure for the dance
                  community.
                </DimmedText>
              </Stack>

              <Grid gutter="lg">
                {PROJECTS.map((project) => (
                  <Grid.Col key={project.name} span={{ base: 12, md: 6 }}>
                    <Card padding="xl" radius="md" h="100%">
                      <Stack gap="md" h="100%">
                        <Group justify="space-between" align="flex-start">
                          <ThemeIcon
                            size={52}
                            radius="md"
                            style={{
                              background: `linear-gradient(135deg, ${project.color}33, ${project.color}11)`,
                              border: `1px solid ${project.color}33`,
                            }}
                          >
                            <project.icon
                              size={26}
                              style={{ color: project.color }}
                            />
                          </ThemeIcon>
                          <Badge variant={project.badge} size="sm">
                            {project.badgeLabel}
                          </Badge>
                        </Group>

                        <Stack gap="xs">
                          <Heading3>{project.name}</Heading3>
                          <DimmedText
                            size="sm"
                            style={{ color: project.color, fontWeight: 600 }}
                          >
                            {project.tagline}
                          </DimmedText>
                        </Stack>

                        <MainText
                          style={{ lineHeight: 1.65, flex: 1 }}
                        >
                          {project.description}
                        </MainText>

                        <Anchor
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="never"
                        >
                          <Button
                            variant="outline"
                            fullWidth
                            rightSection={<IconExternalLink size={16} />}
                            styles={{
                              root: {
                                borderColor: `${project.color}44`,
                                color: project.color,
                              },
                            }}
                          >
                            {project.cta}
                          </Button>
                        </Anchor>
                      </Stack>
                    </Card>
                  </Grid.Col>
                ))}
              </Grid>
            </Stack>

            {/* ── CTA ── */}
            <Card padding="xl" radius="md">
              <Stack gap="md" align="center" py="md">
                <Heading2 variant="shadow" style={{ textAlign: "center" }}>
                  Ready to Dance?
                </Heading2>
                <DimmedText
                  size="lg"
                  style={{
                    textAlign: "center",
                    maxWidth: "clamp(260px, 80vw, 480px)",
                  }}
                >
                  Whether you&apos;re brand new or looking to level up, I&apos;d
                  love to dance with you. Reach out to book a lesson or ask
                  about upcoming workshops.
                </DimmedText>
                <Group gap="md" mt="xs">
                  <Button
                    component={Link}
                    href="/contact"
                    variant="gradient"
                    gradient={{ from: "#b8843d", to: "#d4552b", deg: 135 }}
                    size="lg"
                  >
                    Get in Touch
                  </Button>
                  <Button
                    component="a"
                    href="https://instagram.com/mr.neokiz"
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtle"
                    size="lg"
                    leftSection={<IconBrandInstagram size={18} />}
                    styles={{ root: { color: "#b8843d" } }}
                  >
                    Follow on Instagram
                  </Button>
                </Group>
              </Stack>
            </Card>

            {/* ── Latest Posts ── */}
            <LatestPostsStrip pillar="dance" />
        </Stack>
      </PageShell>
    </>
  );
}
